import request from 'api/requestUtils'

const METASEARCH_DISCOUNT_API_TIMEOUT_MS = 5000

export interface ResolveMetasearchDiscountInput {
  /**
   * Google's signed discount token. Optional since GHA-585: Google leaves RATE-PLAN-ID empty on
   * a sizeable share of clicks, and `rateRuleId` carries the fallback instead.
   */
  token?: string
  propertyId: string
  checkIn: string
  checkOut: string
  /** Full displayed price, inclusive of property fees. Also the request cache key. */
  price: number
  /**
   * Property-fees portion of `price` (payable at the property). Forwarded so the resolver can
   * base margin_retention on the inclusive price (`price − propertyFees`). Not part of the cache
   * key — `price` alone keys the request, keeping write/read lookups aligned.
   */
  propertyFees?: number
  occupancy: Array<string>
  currency?: string
  mx?: string
  /**
   * Google's raw stored rate-rule ID, forwarded verbatim (never parsed, normalised, or defaulted).
   * Optional so non-Google and already-cached v1/v2 traffic stays valid. Part of the request key.
   */
  rateRuleId?: string
  /** The selected rate method; matched_gha receives only the buffer discount. */
  calculationMethod?: string
  /**
   * Google's displayed total from the landing URL (`priceTotal`) and the currency it is quoted
   * in. Sent together or not at all: it stands in for the price a signed token would have baked,
   * so the resolver's price buffer can still hold the number Google showed (GHA-585). A price
   * without its currency cannot be compared against the charged one, so it buys nothing.
   */
  advertisedPrice?: number
  advertisedCurrency?: string
}

export interface ResolveMetasearchDiscountResponse {
  discountAmount: number
}

export function resolveMetasearchDiscount(
  input: ResolveMetasearchDiscountInput,
): Promise<ResolveMetasearchDiscountResponse> {
  return request.post<ResolveMetasearchDiscountResponse, ResolveMetasearchDiscountInput>(
    '/api/connections/google/discount-resolve',
    input,
    { timeout: METASEARCH_DISCOUNT_API_TIMEOUT_MS },
  ).then(response => ({
    discountAmount: response?.discountAmount ?? 0,
  })).catch(() => ({
    discountAmount: 0,
  }))
}
